import { XMarkIcon } from "@heroicons/react/24/outline"
import classNames from "classnames"
import { useTranslate } from "hooks/useTranslate"
import { PropsWithChildren } from "react"
import { ToastContainer, ToastContainerProps } from "react-toastify"

import "react-toastify/dist/ReactToastify.css"

enum NotificationType {
  Info = "info",
  Success = "success",
  Warning = "warning",
  Error = "error",
  Default = "default",
}

const CONTEXT_CLASS: Record<NotificationType, string> = {
  info: "!bg-blue-100 text-blue-500",
  success: "!bg-green-100 text-green-500",
  warning: "!bg-yellow-100 text-yellow-500",
  error: "!bg-red-100 text-red-500",
  default: "!bg-white text-gray-900",
}

const PROGRESS_CLASS: Record<NotificationType, string> = {
  info: "!bg-blue-200",
  success: "!bg-green-200",
  warning: "!bg-yellow-200",
  error: "!bg-red-200",
  default: "bg-white",
}

export interface NotificationProps extends PropsWithChildren<ToastContainerProps> {
  message?: string
}

/**
 * Displays notification to grab attention. The notification typically
 * disappears after a few seconds. Internally this uses `react-toastify`:
 * @link https://github.com/fkhadra/react-toastify
 *
 * @see 'src/hooks/toast.ts' where notification toasts are created.
 * These end up being shown within this component. Therefore this component should
 * always be added top-level (e.g., `App.tsx`).
 */
export const Notification = ({ ...props }: NotificationProps) => {
  const t = useTranslate()

  return (
    <ToastContainer
      data-testid="Alert"
      closeButton={(close) => (
        <button type="button" onClick={close.closeToast} className="self-start">
          <span className="sr-only">{t("button.close")}</span>
          <XMarkIcon className="ml-1 size-5" />
        </button>
      )}
      {...props}
      toastClassName={(context) =>
        classNames(
          // `!items-start` keeps the icon next to the first line of a message that wraps,
          // where react-toastify centers it vertically
          "relative flex p-3 h-full rounded-md !items-start justify-center overflow-hidden cursor-pointer my-2",
          CONTEXT_CLASS[context?.type || "default"]
        )
      }
      className="!m-2 flex !max-w-[92%] items-start"
      progressClassName={(context) =>
        classNames(
          PROGRESS_CLASS[context?.type || "default"],
          "Toastify__progress-bar Toastify__progress-bar--animated Toastify__progress-bar-theme--colored"
        )
      }
    />
  )
}
